import {ReactQueryDevtools} from '@tanstack/react-query-devtools'
import * as React from 'react'
import {Rollbar, SSR, WINDOW, debugging, logger, toLongId} from 'tizra'
import {useAuthConfig} from '../SignInBlock'
import * as B from '../block'
import {Admin} from './admin'

const log = logger('quickstart/blocks/GlobalBlock')

const ReactQueryDevtoolsProduction = React.lazy(() =>
  import('@tanstack/react-query-devtools/build/lib/index.prod.js').then(d => ({
    default: d.ReactQueryDevtools,
  })),
)

export const GlobalBlock = B.blockWithInfo(
  {
    name: 'global',
    displayName: 'GlobalBlock',
    title: 'Global', // just for storybook
    wrapperDiv: false,
    Admin,
  },
  () => {
    useRollbarSetup()
    return (
      <>
        {!SSR && (
          <>
            <ReactQueryDevtools />
            {debugging() && (
              <React.Suspense fallback={null}>
                <ReactQueryDevtoolsProduction />
              </React.Suspense>
            )}
          </>
        )}
        {useLoginLogout(useAuthConfig())}
      </>
    )
  },
)

function useRollbarSetup() {
  const userData = B.useUserData()

  React.useEffect(() => {
    // userData will be null before the API call completes, then will transition
    // to either false or an object. We only care if it's truthy.
    if (!Rollbar || !userData) {
      return
    }
    const config = {
      payload: {
        person: {
          id: `${toLongId(userData.tizraId)}`,
          username: `${userData.name}`,
        },
      },
    }
    log.debug?.('configuring Rollbar', config)
    Rollbar.configure(config)
  }, [userData])
}

const authConfigKeys = {
  register: 'registration',
  resetPassword: 'passwordReset',
  signin: 'signin',
} as const

function useLoginLogout(authConfig: ReturnType<typeof useAuthConfig>) {
  const state = B.SignInModal.useModalState()
  const {show} = state

  React.useEffect(() => {
    log.debug?.('in useLoginLogout effect')
    const window = WINDOW!
    Object.assign(window.tizra, {
      login: (view: B.SignInModalView = 'signin') => {
        if (view === 'signin' && window.tizra.customLoginUrl) {
          window.location.assign(window.tizra.customLoginUrl)
          return
        }
        const k = authConfigKeys[view]
        if (!k) {
          log.error('unknown view', view)
          return
        }
        if (authConfig.modal.enabled) {
          show(view)
        } else {
          const [url, params = ''] = authConfig[k].url.split('?', 2)
          const usp = new URLSearchParams(params)
          usp.set(
            'successUrl',
            window.location.pathname +
              window.location.search +
              window.location.hash,
          )
          window.location.assign(url + '?' + usp)
        }
      },
      logout: () => {
        window.location.assign(
          window.tizra.customLogoutUrl ||
            '/authcallback/_/internal?operation=logout',
        )
      },
      registerUser: () => {
        window.tizra.login('register')
      },
      resetPassword: () => {
        window.tizra.login('resetPassword')
      },
    })
  }, [authConfig, show])

  return <B.SignInModal state={state} authConfig={authConfig} />
}
